/**
 * Validates whether a URL is safe to use as a return URL after authentication.
 *
 * Security validations:
 * - Must be a relative URL starting with '/' (prevents open redirects to external sites)
 * - Must not be a protocol-relative URL '//' (prevents redirects to evil.com)
 * - Must not be the auth callback URL itself (prevents infinite loops)
 * - Must be longer than just '/' (we reject root and want meaningful URLs)
 *
 * @param url - The URL to validate (from RouterStateSnapshot.url or sessionStorage)
 * @returns true if the URL is valid and safe to use as a return URL
 *
 * @example
 * isValidReturnUrl('/dashboard') // true
 * isValidReturnUrl('/auth/callback') // false (prevents loops)
 * isValidReturnUrl('//evil.com') // false (prevents open redirects)
 * isValidReturnUrl('https://evil.com') // false (prevents external redirects)
 * isValidReturnUrl('/') // false (root is not meaningful)
 * isValidReturnUrl('') // false (empty is invalid)
 * isValidReturnUrl(null) // false (null is invalid)
 */
export function isValidReturnUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }

  return (
    url.startsWith('/') &&
    !url.startsWith('//') &&
    !(
      url === '/auth/callback' ||
      url.startsWith('/auth/callback?') ||
      url.startsWith('/auth/callback/') ||
      url.startsWith('/auth/callback;') ||
      url.startsWith('/auth/callback#')
    ) &&
    url.length > 1
  );
}
